import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import Hidden from "@insite/mobius/Hidden";
import { IconWrapper } from "@insite/mobius/Icon";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import BubbleDots from "@store/Components/Icons/Feedback/BubbleDots";
import BubbleEmptyLeft from "@store/Components/Icons/Feedback/BubbleEmptyLeft";
import BubbleEmptyRight from "@store/Components/Icons/Feedback/BubbleEmptyRight";
import BubbleLines from "@store/Components/Icons/Feedback/BubbleLines";
import BubblePen from "@store/Components/Icons/Feedback/BubblePen";
import BubbleWarning from "@store/Components/Icons/Feedback/BubbleWarning";
import { COLORS, COMMON, TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import Color from "color";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

const enum fields {
    variant = "variant",
    useBoxShadow = "useBoxShadow",
    link = "link",
    icon = "icon",
    buttonText = "buttonText",
    showTextMobile = "showTextMobile",
}

const enum icons {
    bubbleDots = "BubbleDots",
    bubbleEmptyLeft = "BubbleEmptyLeft",
    bubbleEmptyRight = "BubbleEmptyRight",
    bubbleLines = "BubbleLines",
    bubblePen = "BubblePen",
    bubbleWarning = "BubbleWarning",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.variant]: "primary" | "secondary" | "tertiary";
        [fields.useBoxShadow]: boolean;
        [fields.link]: string;
        [fields.icon]: string;
        [fields.buttonText]: string;
        [fields.showTextMobile]: boolean;
    };
}

export interface FeedbackStyles {
    feedbackButton?: ButtonPresentationProps;
    feedbackIcon?: ButtonIconPresentationProps;
}

export const feedbackStyles: FeedbackStyles = {
    feedbackButton: {
        css: css`
            padding: 12px;
            border-radius: 50%;

            &.hasText {
                padding: 8px;
                border-radius: 10px;
                display: flex;
                justify-content: center;
                align-items: center;
                font-weight: ${TYPOGRAPHY.weight.bold};

                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [{ values: ["0 0 0 auto", null, null, "auto", null], properties: ["margin"] }],
                        mediaOptions.min,
                    )}

                ${IconWrapper} {
                    margin-right: 5px;
                }
            }
        `,
    },
    feedbackIcon: {
        size: 28,
    },
};

const styles = feedbackStyles;

const GetIcon = (icon: string) => {
    switch (icon) {
        case icons.bubbleDots:
            return BubbleDots;
        case icons.bubbleEmptyLeft:
            return BubbleEmptyLeft;
        case icons.bubbleEmptyRight:
            return BubbleEmptyRight;
        case icons.bubbleLines:
            return BubbleLines;
        case icons.bubblePen:
            return BubblePen;
        case icons.bubbleWarning:
            return BubbleWarning;
        default:
            break;
    }

    return "";
};

const Feedback: FC<OwnProps> = ({ fields }) => {
    const { variant, useBoxShadow, link, icon, buttonText, showTextMobile } = fields;
    const [currentWidth, setCurrentWidth] = useState(0);
    const isMobile = currentWidth < COMMON.breakpoints[3];

    const handleResize = () => {
        setCurrentWidth(window.innerWidth);
    };

    useEffect(() => {
        setCurrentWidth(window.innerWidth);
        window.addEventListener("resize", handleResize, true);
    }, []);

    const feedbackButtonStyle = {
        css: css`
            ${useBoxShadow &&
            `
                -webkit-box-shadow: 0px 0px 10px 0px ${Color(COLORS.black).alpha(0.4).string()};
                -moz-box-shadow: 0px 0px 10px 0px ${Color(COLORS.black).alpha(0.4).string()};
                box-shadow: 0px 0px 10px 0px ${Color(COLORS.black).alpha(0.4).string()};
            `}
            ${styles.feedbackButton?.css}
        `,
    };

    return (
        <Button
            {...feedbackButtonStyle}
            variant={variant}
            onClick={() => window.open(link, "_blank")}
            className={(buttonText && !isMobile) || (buttonText && isMobile && showTextMobile) ? "hasText" : ""}
        >
            <ButtonIcon {...styles.feedbackIcon} src={GetIcon(icon)} color={variant ? COLORS.white : COLORS.black} />
            {showTextMobile ? buttonText : <Hidden below="lg">{buttonText}</Hidden>}
        </Button>
    );
};

const widgetModule: WidgetModule = {
    component: Feedback,
    definition: {
        group: "Basic",
        displayName: "Feedback Button",
        icon: "rectangle-wide",
        fieldDefinitions: [
            {
                name: fields.variant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                name: fields.useBoxShadow,
                displayName: "Add shadow behind button",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                variant: "toggle",
            },
            {
                name: fields.link,
                displayName: "Feedback Link",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.icon,
                displayName: "Icon",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Bubble with Dots", value: icons.bubbleDots },
                    { displayName: "Empty Bubble Pointing Left", value: icons.bubbleEmptyLeft },
                    { displayName: "Empty Bubble Pointing Right", value: icons.bubbleEmptyRight },
                    { displayName: "Bubble with Lines", value: icons.bubbleLines },
                    { displayName: "Bubble with Pen", value: icons.bubblePen },
                    { displayName: "Bubble Warning", value: icons.bubbleWarning },
                ],
                defaultValue: "TextWarning",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.buttonText,
                displayName: "Text to display",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                maxLength: 15,
                tooltip: "Max 15 characters",
            },
            {
                name: fields.showTextMobile,
                displayName: "Display text on mobile as well",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                variant: "toggle",
                isVisible: widget => widget.fields.buttonText,
            },
        ],
    },
};

export default widgetModule;
